import { zodResolver } from '@hookform/resolvers/zod';
import { Loader2, LogIn } from 'lucide-react';
import { useState } from 'react';
import { useForm } from 'react-hook-form';
import { toast } from 'sonner';
import { z } from 'zod';

import {
  isMfaChallenge,
  useNativeLogin,
  type AuthTenant,
} from '@/entities/auth';
import { getApiErrorMessage, getApiErrorStatus } from '@/shared/api';
import { Button, Form } from '@/shared/ui';

import { AuthTextField } from '../AuthTextField';
import { AUTH_HINT, AUTH_TEXT } from '../texts';
import { AuthNotice } from './AuthNotice';
import { GoogleLoginButton, OrDivider } from './GoogleLoginButton';

const schema = z.object({
  password: z.string().min(1, 'Введите пароль'),
});

type PasswordForm = z.infer<typeof schema>;

/**
 * Вход в выбранную компанию. Поле пароля — только если в `methods` есть
 * `password`; кнопка Google есть всегда.
 */
export function PasswordStep({
  email,
  tenant,
  onChangeEmail,
  onChangeTenant,
  onMfaRequired,
  onLoggedIn,
}: {
  email: string;
  tenant: AuthTenant;
  onChangeEmail: () => void;
  /** Нет, если почта заведена только в одной компании. */
  onChangeTenant?: () => void;
  onMfaRequired: (mfaToken: string) => void;
  onLoggedIn: () => void;
}) {
  const form = useForm<PasswordForm>({
    resolver: zodResolver(schema),
    defaultValues: { password: '' },
  });
  const login = useNativeLogin();
  const [rateLimited, setRateLimited] = useState(false);

  const onSubmit = form.handleSubmit(({ password }) => {
    setRateLimited(false);
    login.mutate(
      { tenant_slug: tenant.slug, email, password },
      {
        onSuccess: (response) => {
          if (isMfaChallenge(response)) {
            onMfaRequired(response.mfa_token);
            return;
          }
          onLoggedIn();
        },
        onError: (error) => {
          const status = getApiErrorStatus(error);

          // Бэк намеренно не уточняет, что неверно — почта или пароль.
          if (status === 401) {
            form.setError('password', {
              message: AUTH_TEXT.invalidCredentials,
            });
            return;
          }
          if (status === 429) {
            setRateLimited(true);
            return;
          }
          toast.error(getApiErrorMessage(error, 'Не удалось войти'));
        },
      }
    );
  });

  const busy = login.isPending;
  const hasPassword = tenant.methods.includes('password');

  return (
    <div className="grid gap-4">
      <div className="rounded-xl border border-slate-200 bg-slate-50 px-4 py-3">
        <div className="flex items-center justify-between gap-3">
          <span className="min-w-0 truncate text-[15px] text-slate-900">
            {email}
          </span>
          <button
            type="button"
            onClick={onChangeEmail}
            disabled={busy}
            className="shrink-0 text-sm font-medium text-[var(--primary)] hover:underline disabled:opacity-60"
          >
            Изменить
          </button>
        </div>
        {onChangeTenant ? (
          <button
            type="button"
            onClick={onChangeTenant}
            disabled={busy}
            className="mt-1 text-xs font-medium text-[var(--primary)] hover:underline disabled:opacity-60"
          >
            Выбрать другую компанию
          </button>
        ) : null}
      </div>

      {hasPassword ? (
        <>
          <Form {...form}>
            <form onSubmit={onSubmit} noValidate className="grid gap-4">
              {/* Почта для менеджера паролей: её поле осталось на прошлом шаге. */}
              <input
                type="email"
                name="email"
                autoComplete="username"
                value={email}
                readOnly
                hidden
              />

              <AuthTextField
                control={form.control}
                name="password"
                label="Пароль"
                type="password"
                placeholder="Ваш пароль"
                autoComplete="current-password"
                autoFocus
                disabled={busy}
              />

              {rateLimited ? (
                <AuthNotice
                  tone="error"
                  text={AUTH_TEXT.loginRateLimited}
                  hint={AUTH_HINT.rateLimited}
                />
              ) : null}

              <Button
                type="submit"
                size="lg"
                disabled={busy}
                className="mt-2 w-full rounded-xl"
              >
                {busy ? (
                  <>
                    <Loader2 className="h-4 w-4 animate-spin" />
                    Входим…
                  </>
                ) : (
                  <>
                    <LogIn className="h-4 w-4" />
                    Войти
                  </>
                )}
              </Button>
            </form>
          </Form>

          <OrDivider />
        </>
      ) : null}

      <GoogleLoginButton disabled={busy} />

      {tenant.methods.includes('google') ? (
        <p className="text-center text-xs text-slate-500">
          Вы уже входили в эту компанию через Google
        </p>
      ) : null}
    </div>
  );
}
